import React, { useMemo } from "react";

import moment from "moment";
import { useController, useFormContext } from "react-hook-form";

import DateTimePicker from "@/src/components/common/tools/DateTimePicker";
import { Field, FieldError, FieldLabel } from "@/src/components/ui/field";

import type { JSX } from "react";
import type { FieldPathByValue, FieldValues } from "react-hook-form";

interface DateTimeFieldBaseProps {
  label: string;
  value: Date | undefined;
  onChange: (date: Date) => void;
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
  error?: string;
}

export function DateTimeFieldBase({
  label,
  value,
  onChange,
  disabled,
  minDate,
  maxDate,
  error,
}: DateTimeFieldBaseProps): JSX.Element {
  return (
    <Field>
      <FieldLabel>{label}</FieldLabel>
      <DateTimePicker
        disabled={disabled}
        maxDate={maxDate}
        minDate={minDate}
        onDateTimeChange={onChange}
        value={value}
      />
      <FieldError>{error}</FieldError>
    </Field>
  );
}

interface DateTimeFieldProps<T extends FieldValues> {
  name: FieldPathByValue<T, string | undefined>;
  label: string;
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
}

/** Binds a DateTimePicker to an ISO 8601 string form value. */
function DateTimeField<T extends FieldValues>({
  name,
  label,
  disabled,
  minDate,
  maxDate,
}: DateTimeFieldProps<T>): JSX.Element {
  const { control } = useFormContext();
  const { field, fieldState } = useController({ control, name });
  const dateValue = useMemo(
    () => (field.value ? new Date(field.value) : undefined),
    [field.value],
  );

  return (
    <DateTimeFieldBase
      disabled={disabled}
      error={fieldState.error?.message}
      label={label}
      maxDate={maxDate}
      minDate={minDate}
      onChange={(date) => field.onChange(moment(date).format())}
      value={dateValue}
    />
  );
}

export default DateTimeField;
